<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import type { BusinessWebTemplate } from '~/types';

const { t } = useI18n();

interface BusinessWebTemplateOption {
  id: BusinessWebTemplate;
  name: string;
  description: string;
  preview: string;
  image?: string;
  label?: string;
}

const props = withDefaults(defineProps<{
  modelValue: BusinessWebTemplate;
  description?: boolean;
  switchToMobile?: boolean;
  showPreview?: boolean;
  compact?: boolean;
}>(), {
  description: false,
  switchToMobile: false,
  showPreview: false,
  compact: false
});

const { modelValue, description } = toRefs(props);

const emit = defineEmits<{
  'update:modelValue': [value: BusinessWebTemplate];
}>();

// Signature first: it is the default a new web CV gets. Studio follows.
const businessWebTemplates: BusinessWebTemplateOption[] = [
  {
    id: 'signature',
    name: t('cv.businessWebTemplates.signature.name') || 'Signature',
    description: t('cv.businessWebTemplates.signature.description') || 'Your colour, typeface and section order, set like the PDF',
    preview: '🎨',
    image: '/web-signature.png',
    label: 'New'
  },
  {
    id: 'sofax',
    name: t('cv.businessWebTemplates.sofax.name') || 'Studio',
    description: t('cv.businessWebTemplates.sofax.description') || 'Editorial layout in the InMyResume design language',
    preview: '✨',
    image: '/web-sofax.png',
    label: 'New'
  },
  {
    id: 'classic',
    name: t('cv.businessWebTemplates.classic.name') || 'Classic',
    description: t('cv.businessWebTemplates.classic.description') || 'Traditional professional business web layout',
    preview: '📄',
    image: '/web-classic.png'
  },
  {
    id: 'modern',
    name: t('cv.businessWebTemplates.modern.name') || 'Modern',
    description: t('cv.businessWebTemplates.modern.description') || 'Contemporary business web design with modern elements',
    preview: '🎨',
    image: '/web-modern.png',
    label: 'Preview'
  },
];

const selectedTemplate = computed({
  get: () => modelValue.value,
  set: (value: BusinessWebTemplate) => emit('update:modelValue', value)
});

// Every account can pick any web layout; the web CV never carries a watermark.
const handleTemplateSelect = (template: BusinessWebTemplateOption) => {
  selectedTemplate.value = template.id;
};
</script>

<template>
  <div :class="compact ? 'inline-flex' : 'w-full'">
    <!-- Compact inline pill strip — whole strip scales up on hover -->
    <div
      v-if="compact"
      class="inline-flex origin-top-left items-center gap-1.5 rounded-xl border border-gray-200 bg-white p-1.5 shadow-sm transition-transform duration-300 ease-out hover:z-30 hover:scale-[1.8] hover:shadow-2xl dark:border-gray-700 dark:bg-gray-800"
    >
      <button
        v-for="template in businessWebTemplates"
        :key="template.id"
        type="button"
        :title="template.description"
        class="relative flex flex-col items-center gap-1.5 rounded-lg p-2 text-xs font-semibold transition-colors"
        :class="selectedTemplate === template.id
          ? 'bg-primary/10 text-primary ring-1 ring-primary/40 dark:bg-primary/25 dark:text-white'
          : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white'"
        @click="handleTemplateSelect(template)"
      >
        <div
          class="relative h-16 w-24 shrink-0 overflow-hidden rounded border border-gray-200 bg-white shadow-sm dark:border-gray-600"
          :class="selectedTemplate === template.id ? 'ring-2 ring-primary/50' : ''"
        >
          <NuxtImg
            v-if="template.image"
            :src="template.image"
            :alt="`${template.name} preview`"
            :width="280"
            :height="200"
            format="webp"
            :quality="90"
            class="size-full object-cover object-top"
            loading="lazy"
          />
          <!-- A template without a screenshot yet still needs a tile to click -->
          <div v-else class="flex size-full items-center justify-center bg-gray-50 text-xl dark:bg-gray-700">
            {{ template.preview }}
          </div>
          <div
            v-if="selectedTemplate === template.id"
            class="absolute right-1 top-1 flex size-4 items-center justify-center rounded-full bg-primary text-white shadow"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="size-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="4" d="M5 13l4 4L19 7" />
            </svg>
          </div>
        </div>
        <span>{{ template.name }}</span>
      </button>
    </div>

    <!-- Mobile Select (when description prop is true) -->
    <div v-if="!compact && description" class="flex md:hidden">
      <select
:value="selectedTemplate"
        class="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary dark:border-gray-600 dark:bg-gray-800 dark:text-white"
        @change="selectedTemplate = ($event.target as HTMLSelectElement).value as BusinessWebTemplate">
        <option v-for="template in businessWebTemplates" :key="template.id" :value="template.id">
          {{ showPreview ? '🖼️' : template.preview }} {{ template.name }}
        </option>
      </select>
    </div>

    <!-- Desktop Grid (always visible, hidden on mobile when description is true) -->
    <div v-if="!compact" :class="description ? 'hidden md:grid' : 'grid'" class="w-full gap-3 sm:grid-cols-1 md:grid-cols-2">
      <CommonButton
        v-for="template in businessWebTemplates"
        :key="template.id"
        class="group relative cursor-pointer rounded-lg border p-2 text-left transition-all hover:-translate-y-0.5 hover:shadow-lg"
        :class="{
          'flex flex-col': showPreview,
          'border-primary bg-primary/5 ring-1 ring-primary/30': selectedTemplate === template.id,
          'border-gray-200 hover:border-primary/40 dark:border-gray-700 dark:hover:border-primary/60': selectedTemplate !== template.id
        }"
        @click="handleTemplateSelect(template)">

        <!-- Selected indicator -->
        <div
          v-if="selectedTemplate === template.id"
          class="absolute -right-2 -top-2 z-10 flex size-5 items-center justify-center rounded-full bg-primary text-white shadow-md">
          <svg xmlns="http://www.w3.org/2000/svg" class="size-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7" />
          </svg>
        </div>

        <div class="flex flex-row items-center gap-3">
          <!-- Thumbnail preview (web = landscape) -->
          <div
            class="relative h-10 w-14 shrink-0 overflow-hidden rounded-sm border border-gray-200 bg-white shadow-sm transition-transform group-hover:scale-105 dark:border-gray-600"
            :class="selectedTemplate === template.id ? 'ring-1 ring-primary/40' : ''"
          >
            <img
              v-if="template.image"
              :src="template.image"
              :alt="`${template.name} template preview`"
              class="size-full object-cover object-top"
              loading="lazy"
            >
            <div v-else class="flex size-full items-center justify-center text-base">
              {{ template.preview }}
            </div>
          </div>

          <div class="min-w-0 flex-1">
            <div class="!text-sm font-semibold text-gray-900 dark:text-gray-100">
              {{ template.name }}
            </div>
            <div class="truncate text-[11px] text-gray-500 dark:text-gray-400">
              {{ template.description }}
            </div>
          </div>
        </div>

        <div v-if="showPreview" class="mb-3">
          <img
            v-if="template.image"
            :src="template.image"
            :alt="`${template.name} template preview`"
            class="h-52 w-full rounded-md border border-gray-200 object-cover dark:border-gray-600">
        </div>
      </CommonButton>
    </div>

  </div>
</template>
